import { useCallback, useState } from "react";
import type { BrandIdentity, BrandConfig, BrandDirectorySummary } from "@/config/brand";
import { useQueryClient } from "@tanstack/react-query";
import {
  brandsKeys,
  useActiveBrandId,
  useBrandConfig,
  useBrandsList,
  useCreateBrand,
  useDeleteBrand,
  useSaveBrand,
  useSetActiveBrand,
} from "./useBrandsList";

interface UseBrandsReturn {
  /** Backend's `/brands/` returns the richer `BrandDirectorySummary` shape
   *  (palette / logo / tagline) — kept here so list-level UI like
   *  `BrandListRow` can render the monogram + logo inline without an extra
   *  per-brand fetch. Consumers that only need identity fields stay
   *  source-compat because `BrandDirectorySummary extends BrandIdentity`. */
  brands: BrandDirectorySummary[];
  activeBrandId: string;
  selectedBrand: BrandConfig | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  selectBrand: (id: string | null) => void;
  setActiveBrand: (id: string) => Promise<void>;
  saveBrand: (brand: BrandConfig) => Promise<void>;
  createBrand: (brand: BrandConfig) => Promise<BrandIdentity>;
  deleteBrand: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * useBrands — composition over the brand RQ hooks.
 *
 * Public signature unchanged from the pre-Phase-6 implementation.
 * Internals now delegate to React Query: query cache replaces the
 * useState mirrors, useMutation replaces the manual callbacks. The
 * single piece of true UI state is `selectedId` (which brand the user
 * has open in the editor).
 */
export function useBrands(): UseBrandsReturn {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const brandsQuery = useBrandsList();
  const activeBrandQuery = useActiveBrandId();
  const selectedBrandQuery = useBrandConfig(selectedId);

  const setActiveMutation = useSetActiveBrand();
  const saveMutation = useSaveBrand();
  const createMutation = useCreateBrand();
  const deleteMutation = useDeleteBrand();

  const loading = brandsQuery.isLoading || activeBrandQuery.isLoading;
  const saving =
    saveMutation.isPending || createMutation.isPending || deleteMutation.isPending;

  // Surface the first error in the same string-or-null shape the legacy
  // hook returned. Mutation errors take precedence over query errors so
  // a failed action doesn't get masked by a stale fetch error.
  const errorSource =
    setActiveMutation.error ??
    saveMutation.error ??
    createMutation.error ??
    deleteMutation.error ??
    brandsQuery.error ??
    activeBrandQuery.error ??
    selectedBrandQuery.error ??
    null;
  const error = errorSource
    ? errorSource instanceof Error
      ? errorSource.message
      : String(errorSource)
    : null;

  const selectBrand = useCallback((id: string | null) => {
    setSelectedId(id);
  }, []);

  const setActiveBrand = useCallback(
    async (id: string) => {
      await setActiveMutation.mutateAsync(id).catch(() => {
        // Error is surfaced via setActiveMutation.error above; the
        // legacy callback also swallowed throws.
      });
    },
    [setActiveMutation],
  );

  const saveBrand = useCallback(
    async (brand: BrandConfig) => {
      await saveMutation.mutateAsync(brand).catch(() => {
        // ditto — error surfaced via saveMutation.error.
      });
    },
    [saveMutation],
  );

  const createBrand = useCallback(
    async (brand: BrandConfig): Promise<BrandIdentity> => {
      const created = await createMutation.mutateAsync(brand);
      setSelectedId(created.id);
      return created;
    },
    [createMutation],
  );

  const deleteBrand = useCallback(
    async (id: string) => {
      await deleteMutation.mutateAsync(id).catch(() => {
        // legacy parity: swallow throw, error surfaced via state.
      });
      if (selectedId === id) {
        setSelectedId(null);
      }
    },
    [deleteMutation, selectedId],
  );

  const refresh = useCallback(async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: brandsKeys.list() }),
      qc.invalidateQueries({ queryKey: brandsKeys.active() }),
    ]);
  }, [qc]);

  return {
    brands: brandsQuery.data ?? [],
    activeBrandId: activeBrandQuery.data ?? "",
    selectedBrand: selectedBrandQuery.data ?? null,
    loading,
    saving,
    error,
    selectBrand,
    setActiveBrand,
    saveBrand,
    createBrand,
    deleteBrand,
    refresh,
  };
}
